import { lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  FlaskConical,
  FolderKanban,
  Library,
  LogOut,
  KeyRound,
  LayoutDashboard,
  ScrollText,
  ScanSearch,
} from "lucide-react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import industrialMindLogo from "../assets/industrialmind-logo.png";
import { useAuth } from "../features/auth/AuthProvider";
import { LanguageSwitcher, useI18n } from "../i18n";
import { useFeaturesQuery, useSuitesQuery } from "./queries";
import { useToast } from "./ToastProvider";
import { detailParent, detailReturnTo, navItemForReturnTo } from "./routeContext";

const NewRunDialog = lazy(() => import("../pages/NewRunPage").then((module) => ({ default: module.NewRunDialog })));

const NAV_GROUPS = [
  {
    labelKey: "shell.navGroup.workspace",
    items: [
      { to: "/overview", labelKey: "nav.overview", icon: LayoutDashboard },
      { to: "/projects", labelKey: "nav.projects", icon: FolderKanban },
    ],
  },
  {
    labelKey: "shell.navGroup.testAssets",
    items: [
      { to: "/question-bank", labelKey: "nav.questionBank", icon: Library },
      { to: "/suites", labelKey: "nav.suites", icon: ClipboardList },
    ],
  },
  {
    labelKey: "shell.navGroup.crossProject",
    items: [
      { to: "/queue", labelKey: "nav.queue", icon: ScrollText },
      { to: "/reports", labelKey: "nav.reports", icon: BarChart3 },
      { to: "/cross-evaluations", labelKey: "nav.crossEvaluations", icon: ScanSearch },
    ],
  },
] as const;

/** 路径 → 文档标题文案 key。顺序即优先级，先匹配先用。 */
function documentTitleKey(pathname: string): string {
  if (pathname.startsWith("/overview")) return "shell.title.overview";
  if (pathname.startsWith("/cross-evaluations")) return "shell.title.crossEvaluations";
  if (/\/projects\/[^/]+\/settings\/eval/.test(pathname) || pathname.includes("/evaluation-profiles")) return "shell.title.evaluationProfiles";
  if (/\/projects\/[^/]+\/settings\/prompts/.test(pathname) || (pathname.includes("/prompts") && pathname.startsWith("/projects/"))) return "shell.title.prompts";
  if (/\/projects\/[^/]+\/settings\/env/.test(pathname) || pathname.endsWith("/targets")) return "shell.title.targets";
  if (/\/projects\/[^/]+\/content\/questions/.test(pathname)) return "shell.title.projectQuestions";
  if (/\/projects\/[^/]+\/settings\/sync/.test(pathname) || /\/projects\/[^/]+\/production-samples/.test(pathname)) return "shell.title.dataSync";
  if (/\/projects\/[^/]+\/content/.test(pathname)) return "shell.title.projectContent";
  if (/\/projects\/[^/]+\/(?:tasks|runs)/.test(pathname)) return "shell.title.projectTasks";
  if (/\/projects\/[^/]+\/results/.test(pathname)) return "shell.title.projectResults";
  if (/\/projects\/[^/]+\/reports/.test(pathname)) return "shell.title.projectReports";
  if (pathname.endsWith("/coverage")) return "shell.title.coverage";
  if (pathname.endsWith("/gate-policy")) return "shell.title.gatePolicy";
  if (pathname.endsWith("/notifications")) return "shell.title.gateNotifications";
  if (pathname.endsWith("/settings") && pathname.startsWith("/projects/")) return "shell.title.projectSettings";
  if (pathname.startsWith("/projects")) return "shell.title.projects";
  if (pathname.startsWith("/reports")) return "shell.title.reports";
  if (pathname.startsWith("/question-bank")) return "shell.title.questionBank";
  if (pathname.startsWith("/suites")) return "shell.title.suites";
  if (pathname.startsWith("/queue")) return "shell.title.queue";
  if (pathname.startsWith("/logs")) return "shell.title.logs";
  if (pathname.startsWith("/settings/security")) return "shell.title.security";
  if (/^\/settings\/(?:runtime|rules|prompts)/.test(pathname)) return "shell.title.migrated";
  if (pathname.startsWith("/settings")) return "shell.title.settings";
  if (pathname.startsWith("/new-run") || pathname.endsWith("/new-run")) return "shell.title.newRun";
  if (pathname.startsWith("/runs") && pathname.endsWith("/failures")) return "shell.title.failures";
  if (pathname.startsWith("/runs")) return "shell.title.runDetail";
  return "shell.title.notFound";
}

function isProjectManagementRoute(pathname: string) {
  return /^\/projects(?:\/[^/]+(?:\/.*)?)?\/?$/.test(pathname);
}

function projectIdFromPath(pathname: string) {
  const match = pathname.match(/^\/projects\/([^/]+)/);
  const projectId = match?.[1] ?? "";
  return projectId && projectId !== "default" ? projectId : "";
}

export function AppShell() {
  const auth = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const suitesQuery = useSuitesQuery();
  const featuresQuery = useFeaturesQuery();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("agentJudge.react.sidebarCollapsed") === "true");
  const [menuOpen, setMenuOpen] = useState(false);
  const [newRunOpen, setNewRunOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const menuWrapRef = useRef<HTMLDivElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const setSidebar = (value: boolean) => {
    setCollapsed(value);
    localStorage.setItem("agentJudge.react.sidebarCollapsed", String(value));
  };
  const runParent = location.pathname.startsWith("/runs/")
    ? navItemForReturnTo(
      detailReturnTo(location.search, detailParent(location.state, location.search)),
      detailParent(location.state, location.search),
    )
    : null;
  const visibleRunParent = runParent === "/logs" ? "/queue" : runParent;
  const routeSuiteId = location.pathname.match(/^\/suites\/([^/]+)$/)?.[1] ?? "";
  const currentSuiteId = suitesQuery.data?.items.some((suite) => suite.suite_id === routeSuiteId) ? routeSuiteId : "";
  const currentProjectId = projectIdFromPath(location.pathname);
  const flags = featuresQuery.data;
  const isNavItemVisible = (item: (typeof NAV_GROUPS)[number]["items"][number]) => {
    if (item.to === "/overview") return flags?.OVERVIEW_DASHBOARD_ENABLED !== false;
    if (item.to === "/projects" && flags?.PROJECT_MANAGEMENT_ENABLED === false) return false;
    if (item.to === "/question-bank" && flags?.PROJECT_QUESTION_BANK_ENABLED === false) return false;
    if (item.to === "/cross-evaluations" && flags?.CROSS_EVALUATIONS_ENABLED === false) return false;
    return true;
  };
  const activeNavContext = NAV_GROUPS.flatMap((group) => group.items.map((item) => ({ group, item })))
    .find(({ item }) => {
      const routeActive = item.to === "/projects" && isProjectManagementRoute(location.pathname);
      const standardActive = item.to !== "/projects"
        && (location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
      return routeActive || visibleRunParent === item.to || standardActive;
    });
  const currentPageLabel = t(documentTitleKey(location.pathname));
  const activeNavLabel = activeNavContext ? t(activeNavContext.item.labelKey) : "";
  useEffect(() => {
    setMenuOpen(false);
    setNewRunOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);
  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (menuOpen && !menuWrapRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const closeWithEscape = (event: KeyboardEvent) => {
      if (menuOpen && event.key === "Escape") { setMenuOpen(false); menuTriggerRef.current?.focus(); }
    };
    window.addEventListener("pointerdown", closeOutside);
    window.addEventListener("keydown", closeWithEscape);
    return () => { window.removeEventListener("pointerdown", closeOutside); window.removeEventListener("keydown", closeWithEscape); };
  }, [menuOpen]);
  useEffect(() => {
    document.title = t("shell.documentTitle", { page: t(documentTitleKey(location.pathname)) });
  }, [location.pathname, t]);

  const logout = async () => {
    try { await auth.logout(); }
    catch (error) {
      showToast(t("shell.logoutFailed", { message: error instanceof Error ? error.message : t("shell.logoutFailedFallback") }), "error");
    }
  };

  return (
    <div className={`react-app-shell${collapsed ? " sidebar-collapsed" : ""}`}>
      <a className="skip-to-content" href="#main-content">{t("shell.skipToContent")}</a>
      <aside className="react-sidebar" aria-label={t("shell.primaryNavAria")}>
        <NavLink className="react-brand" to="/overview" aria-label={t("shell.brandAria")}>
          <img className="react-brand-logo" src={industrialMindLogo} alt="" aria-hidden="true" />
          {!collapsed ? <strong>Agent Judge<small>{t("shell.brandSubtitle")}</small></strong> : null}
        </NavLink>
        <button
          className="sidebar-primary-action"
          title={collapsed ? t("nav.newRun") : undefined}
          aria-label={collapsed ? t("nav.newRun") : undefined}
          aria-haspopup="dialog"
          aria-expanded={newRunOpen}
          type="button"
          onClick={() => setNewRunOpen(true)}
        >
          <FlaskConical size={18} />
          {!collapsed ? <span>{t("nav.newRun")}</span> : null}
        </button>
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter(isNavItemVisible);
          if (!items.length) return null;
          return (
            <div className="sidebar-nav-group" key={group.labelKey}>
              {!collapsed ? <span className="sidebar-nav-label">{t(group.labelKey)}</span> : null}
              <nav aria-label={t(group.labelKey)}>
                {items.map((item) => {
                  const routeActive = item.to === "/projects" && isProjectManagementRoute(location.pathname);
                  const standardActive = item.to !== "/projects"
                    && (location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
                  const itemActive = routeActive || visibleRunParent === item.to || standardActive;
                  const label = t(item.labelKey);
                  return (
                    <Link
                      className={itemActive ? "active" : ""}
                      to={item.to}
                      key={item.to}
                      title={collapsed ? label : undefined}
                      aria-current={itemActive ? "page" : undefined}
                    >
                      <item.icon size={19} />
                      {!collapsed ? <span>{label}</span> : null}
                    </Link>
                  );
                })}
              </nav>
            </div>
          );
        })}
        <button className="sidebar-toggle" type="button" onClick={() => setSidebar(!collapsed)} aria-label={collapsed ? t("shell.expandSidebar") : t("shell.collapseSidebar")}>
          {collapsed ? <ChevronsRight size={17} /> : <><ChevronsLeft size={17} /><span>{t("shell.collapse")}</span></>}
        </button>
      </aside>
      <div className="react-main-column">
        <header className="react-topbar">
          <nav className="topbar-breadcrumb" aria-label={t("shell.breadcrumbAria")}>
            <ol>
              {activeNavContext ? <li>{t(activeNavContext.group.labelKey)}</li> : null}
              {activeNavContext ? (
                <li>
                  <ChevronRight size={14} aria-hidden="true" />
                  {currentPageLabel === activeNavLabel
                    ? <span aria-current="page">{activeNavLabel}</span>
                    : <Link to={activeNavContext.item.to}>{activeNavLabel}</Link>}
                </li>
              ) : null}
              {!activeNavContext || currentPageLabel !== activeNavLabel ? (
                <li>
                  {activeNavContext ? <ChevronRight size={14} aria-hidden="true" /> : null}
                  <span aria-current="page">{currentPageLabel}</span>
                </li>
              ) : null}
            </ol>
          </nav>
          <div className="topbar-actions">
            <LanguageSwitcher variant="dropdown" />
            <div className="user-menu-wrap" ref={menuWrapRef}>
              <button ref={menuTriggerRef} className="user-menu-trigger" type="button" onClick={() => setMenuOpen((value) => !value)} aria-expanded={menuOpen} aria-controls="account-menu">
                <span className="user-avatar">{auth.user?.username?.slice(0, 1).toUpperCase() || "A"}</span>
                <span>{auth.user?.username || "admin"}</span>
                <ChevronDown size={15} />
              </button>
              {menuOpen ? (
                <div className="user-menu" id="account-menu">
                  <button type="button" onClick={() => navigate("/settings/security")}><KeyRound size={16} />{t("shell.accountSecurity")}</button>
                  <button type="button" disabled={auth.logoutPending} onClick={() => void logout()}><LogOut size={16} />{auth.logoutPending ? t("shell.loggingOut") : t("shell.logout")}</button>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        <main ref={mainRef} className="react-page-stage" id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      {newRunOpen ? (
        <Suspense fallback={<span className="sr-only" role="status">{t("common.loading")}</span>}>
          <NewRunDialog
            initialProjectId={currentProjectId}
            initialSuiteId={currentSuiteId}
            onClose={() => setNewRunOpen(false)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
